import type { ClientMessage, VoiceData } from "@obean/protocol";
import { signal } from "@preact/signals";
import type { Track } from "../host/telemetry.js";

/**
 * Voice for a room (ADR 0008 Option A, P2-VOICE-02): browser-to-browser WebRTC audio with each other
 * member in voice, signalled through the room (`voice.signal`), relayed by Cloudflare TURN only when
 * a direct path fails. Nothing is recorded. It shares nothing with the game: every failure stays in
 * here (status "unavailable"), and the game and chat carry on.
 *
 * Negotiation is the "perfect negotiation" pattern: either side may offer, and when both do at once
 * the polite one (the larger seat id) gives way.
 */

export type VoiceStatus = "off" | "asking" | "connecting" | "on" | "unavailable";

export interface VoiceDeps {
  send: (msg: ClientMessage) => void;
  credentials: () => Promise<{ iceServers: RTCIceServer[]; turn: boolean }>;
  track: Track;
  /** The microphone: the browser's own consent prompt. */
  microphone?: () => Promise<MediaStream>;
  createPeer?: (config: RTCConfiguration) => RTCPeerConnection;
  createAudio?: () => HTMLAudioElement;
  now?: () => number;
}

interface Peer {
  pc: RTCPeerConnection;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  audio: HTMLAudioElement | null;
  restarted: boolean;
  announced: boolean;
  /** When this connection began: voice_joined's time to first audio counts from here. */
  startedAt: number;
}

/** A level above this (getStats' audioLevel, 0-1) is someone speaking. */
export const SPEAKING_LEVEL = 0.04;
/** How often levels (and first audio) are read. */
const LEVEL_EVERY_MS = 250;

/** `voice_joined.relay` from the chosen candidate pair (docs/metrics.md). */
export function relayOf(
  local: { candidateType?: string; relayProtocol?: string } | undefined,
  remote?: { candidateType?: string },
): "none" | "turn_udp" | "turn_tcp" | "turn_tls" | "turn_peer" {
  if (local?.candidateType === "relay")
    return local.relayProtocol === "tcp"
      ? "turn_tcp"
      : local.relayProtocol === "tls"
        ? "turn_tls"
        : "turn_udp";
  // Relayed on the other side: still a relayed call, through the teammate's TURN allocation.
  return remote?.candidateType === "relay" ? "turn_peer" : "none";
}

export class VoiceSession {
  readonly status = signal<VoiceStatus>("off");
  readonly muted = signal(false);
  /** Why voice is unavailable, in words for the page. */
  readonly problem = signal<string | null>(null);
  /** Seats speaking now; this page's own seat while its mic picks up speech. */
  readonly speaking = signal<ReadonlySet<string>>(new Set());
  readonly #deps: Required<Omit<VoiceDeps, "microphone" | "createPeer" | "createAudio">> &
    Pick<VoiceDeps, "microphone" | "createPeer" | "createAudio">;
  #me: string | null = null;
  #mic: MediaStream | null = null;
  #ice: RTCIceServer[] = [];
  #peers = new Map<string, Peer>();
  #mutedSeats = new Set<string>();
  #joinedAt = 0;
  #levels: ReturnType<typeof setInterval> | undefined;

  constructor(deps: VoiceDeps) {
    this.#deps = { now: () => performance.now(), ...deps };
  }

  /** Joins voice: the microphone (the browser asks), then ICE servers, then tells the room. */
  async join(me: string): Promise<void> {
    if (this.status.value !== "off" && this.status.value !== "unavailable") return;
    this.#me = me;
    this.problem.value = null;
    this.status.value = "asking";
    this.#joinedAt = this.#deps.now();
    try {
      this.#mic = await (this.#deps.microphone ?? defaultMicrophone)();
    } catch (err) {
      this.#deps.track("voice_consent", { granted: false });
      const name = err instanceof Error ? err.name : "error";
      this.#fail(
        "permission",
        name,
        name === "NotAllowedError"
          ? "Voice is unavailable: the microphone isn't allowed. You can still play and chat."
          : "Voice is unavailable: no microphone was found. You can still play and chat.",
      );
      return;
    }
    this.#deps.track("voice_consent", { granted: true });
    // A phone call or another app taking the mic ends the track (lifecycle scenario 4).
    for (const t of this.#mic.getAudioTracks())
      t.addEventListener("ended", () => {
        this.#fail(
          "microphone",
          "ended",
          "Voice stopped: the microphone was taken away. Join again when you're ready.",
        );
      });
    try {
      this.#ice = (await this.#deps.credentials()).iceServers;
    } catch (err) {
      this.#fail(
        "credentials",
        err instanceof Error ? err.message.slice(0, 40) : "error",
        "Voice is unavailable right now. You can still play and chat.",
      );
      return;
    }
    this.status.value = "connecting";
    this.#deps.send({ type: "voice.state", on: true, muted: this.muted.value });
    this.#levels = setInterval(() => {
      void this.#readLevels();
    }, LEVEL_EVERY_MS);
  }

  /** The room's members changed: connect to each one newly in voice, let go of those who left. */
  sync(members: readonly { seat: string; voice?: string | undefined }[]): void {
    if (this.status.value !== "connecting" && this.status.value !== "on") return;
    const inVoice = new Set(
      members.filter((m) => m.seat !== this.#me && m.voice).map((m) => m.seat),
    );
    for (const seat of inVoice) if (!this.#peers.has(seat)) this.#connect(seat);
    for (const seat of [...this.#peers.keys()]) if (!inVoice.has(seat)) this.#close(seat);
    if (this.#peers.size === 0 && this.status.value === "on") this.status.value = "connecting";
  }

  /** A signal from another seat (`voice.signal`). */
  async signal(from: string, data: VoiceData): Promise<void> {
    if (this.status.value !== "connecting" && this.status.value !== "on") return;
    const peer = this.#peers.get(from) ?? this.#connect(from);
    if (!peer) return;
    const { pc } = peer;
    try {
      if ("description" in data) {
        const offer = data.description.type === "offer";
        const collision = offer && (peer.makingOffer || pc.signalingState !== "stable");
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;
        await pc.setRemoteDescription(data.description as RTCSessionDescriptionInit);
        if (offer) {
          await pc.setLocalDescription();
          this.#describe(from, pc);
        }
      } else {
        try {
          await pc.addIceCandidate(data.candidate as RTCIceCandidateInit);
        } catch (err) {
          if (!peer.ignoreOffer) throw err;
        }
      }
    } catch (err) {
      console.warn("voice: a signal from", from, "failed", err);
    }
  }

  /**
   * The room connection is back after a drop: the room cleared this seat's voice with it, so say
   * it again, and start the connections afresh (the other side sees us leave and come back).
   */
  reconnected(): void {
    if (this.status.value !== "connecting" && this.status.value !== "on") return;
    for (const seat of [...this.#peers.keys()]) this.#close(seat);
    this.status.value = "connecting";
    this.#deps.send({ type: "voice.state", on: true, muted: this.muted.value });
  }

  /** Mutes or unmutes this page's microphone, and tells the room (the roster shows it). */
  setMuted(muted: boolean): void {
    this.muted.value = muted;
    for (const t of this.#mic?.getAudioTracks() ?? []) t.enabled = !muted;
    if (this.status.value === "connecting" || this.status.value === "on")
      this.#deps.send({ type: "voice.state", on: true, muted });
  }

  /** Stops hearing one seat on this page (the roster's Mute, with their chat). */
  mutePeer(seat: string, muted: boolean): void {
    if (muted) this.#mutedSeats.add(seat);
    else this.#mutedSeats.delete(seat);
    const audio = this.#peers.get(seat)?.audio;
    if (audio) audio.muted = muted;
  }

  /** Leaves voice: connections closed, the microphone released, the room told. */
  leave(): void {
    if (this.status.value === "off") return;
    const was = this.status.value;
    this.#stop();
    this.status.value = "off";
    if (was === "connecting" || was === "on") {
      this.#deps.send({ type: "voice.state", on: false, muted: false });
      this.#deps.track("voice_left", {
        duration_s: Math.round((this.#deps.now() - this.#joinedAt) / 1000),
      });
    }
  }

  #connect(seat: string): Peer | undefined {
    if (!this.#me || !this.#mic) return undefined;
    let pc: RTCPeerConnection;
    try {
      pc = (this.#deps.createPeer ?? ((c) => new RTCPeerConnection(c)))({ iceServers: this.#ice });
    } catch (err) {
      this.#fail(
        "peer",
        err instanceof Error ? err.name : "error",
        "Voice is unavailable in this browser. You can still play and chat.",
      );
      return undefined;
    }
    const peer: Peer = {
      pc,
      polite: this.#me > seat,
      makingOffer: false,
      ignoreOffer: false,
      audio: null,
      restarted: false,
      announced: false,
      startedAt: this.#deps.now(),
    };
    this.#peers.set(seat, peer);
    for (const t of this.#mic.getAudioTracks()) pc.addTrack(t, this.#mic);
    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        this.#describe(seat, pc);
      } catch (err) {
        console.warn("voice: offer failed", err);
      } finally {
        peer.makingOffer = false;
      }
    };
    pc.onicecandidate = ({ candidate }) => {
      if (candidate)
        this.#send(seat, {
          candidate: candidate.toJSON() as VoiceData extends { candidate: infer C } ? C : never,
        });
    };
    pc.ontrack = ({ streams }) => {
      const audio = peer.audio ?? (this.#deps.createAudio ?? (() => new Audio()))();
      peer.audio = audio;
      audio.autoplay = true;
      audio.muted = this.#mutedSeats.has(seat);
      audio.srcObject = streams[0] ?? null;
      audio.play().catch(() => undefined); // the join tap allowed playback
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") this.status.value = "on";
      if (pc.connectionState === "failed") {
        if (!peer.restarted) {
          peer.restarted = true;
          pc.restartIce(); // one more go, through TURN if a direct path broke
        } else {
          this.#fail(
            "ice",
            "failed",
            "Voice couldn't connect on this network. You can still play and chat.",
          );
        }
      }
    };
    return peer;
  }

  #describe(seat: string, pc: RTCPeerConnection): void {
    const d = pc.localDescription;
    if (d)
      this.#send(seat, {
        description: { type: d.type as "offer" | "answer" | "rollback", sdp: d.sdp },
      });
  }

  #send(to: string, data: VoiceData): void {
    // The type goes first: the room reads a long frame only if it starts as a voice signal.
    this.#deps.send({ type: "voice.signal", to, data });
  }

  /** Who's speaking (getStats' audio levels), and when a peer's audio first arrives. */
  async #readLevels(): Promise<void> {
    const speaking = new Set<string>();
    for (const [seat, peer] of this.#peers) {
      try {
        const stats = await peer.pc.getStats();
        let inbound: { audioLevel?: number; packetsReceived?: number } | undefined;
        let local: { candidateType?: string; relayProtocol?: string } | undefined;
        let remote: { candidateType?: string } | undefined;
        let mine: number | undefined;
        stats.forEach(
          (r: {
            type: string;
            kind?: string;
            audioLevel?: number;
            selectedCandidatePairId?: string;
          }) => {
            if (r.type === "inbound-rtp" && r.kind === "audio") inbound = r;
            if (r.type === "media-source" && r.kind === "audio") mine = r.audioLevel;
            if (r.type === "transport" && r.selectedCandidatePairId) {
              const pair = stats.get(r.selectedCandidatePairId) as
                { localCandidateId?: string; remoteCandidateId?: string } | undefined;
              local = pair?.localCandidateId
                ? (stats.get(pair.localCandidateId) as typeof local)
                : undefined;
              remote = pair?.remoteCandidateId
                ? (stats.get(pair.remoteCandidateId) as typeof remote)
                : undefined;
            }
          },
        );
        if ((inbound?.audioLevel ?? 0) > SPEAKING_LEVEL && !this.#mutedSeats.has(seat))
          speaking.add(seat);
        if (this.#me && !this.muted.value && (mine ?? 0) > SPEAKING_LEVEL) speaking.add(this.#me);
        if (!peer.announced && (inbound?.packetsReceived ?? 0) > 0) {
          peer.announced = true;
          this.#deps.track("voice_joined", {
            relay: relayOf(local, remote),
            // From the call's start, not from joining voice: waiting alone isn't connection time.
            ms_to_first_audio: Math.round(this.#deps.now() - peer.startedAt),
          });
        }
      } catch {
        // A closing connection: nothing to read.
      }
    }
    const before = this.speaking.value;
    if (before.size !== speaking.size || [...speaking].some((s) => !before.has(s)))
      this.speaking.value = speaking;
  }

  #close(seat: string): void {
    const peer = this.#peers.get(seat);
    if (!peer) return;
    this.#peers.delete(seat);
    peer.pc.close();
    if (peer.audio) peer.audio.srcObject = null;
  }

  #stop(): void {
    clearInterval(this.#levels);
    for (const seat of [...this.#peers.keys()]) this.#close(seat);
    for (const t of this.#mic?.getTracks() ?? []) t.stop();
    this.#mic = null;
    this.speaking.value = new Set();
  }

  /** Voice gives up, alone: the page says why, and the game and chat are untouched. */
  #fail(stage: string, code: string, message: string): void {
    const wasIn = this.status.value === "connecting" || this.status.value === "on";
    this.#stop();
    this.status.value = "unavailable";
    this.problem.value = message;
    this.#deps.track("voice_failed", { stage, code });
    if (wasIn) this.#deps.send({ type: "voice.state", on: false, muted: false });
  }
}

function defaultMicrophone(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
}
